// Pure booking-options billing engine. No dependency on Sequelize, moment or currency conversion.
// This file must stay byte-identical in GOZY-API and GOZY-WEB (src/helpers/bookingOptionsBilling.js).

export const OPTION_LIMITS = {
  maxCustomOptions: 3, // default; the live value is the admin setting (normalizeOptionSettings)
  labelMin: 3,
  labelMax: 40,
  descriptionMax: 140,
  deliveryLocationMin: 5,
  deliveryLocationMax: 255,
  maxPrice: 99999999.99,
};

export const BILLING_MODES = ['per_day', 'flat'];

const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

// disabledCodes: fixed options switched off platform-wide by the admin (see normalizeOptionSettings).
export function isOptionBookable(option, disabledCodes = []) {
  return Boolean(option) && Boolean(option.isActive) && !option.adminDisabled && Number(option.price) > 0
    && !disabledCodes.includes(option.code);
}

// Picks the selected options among the listing options and prices them for `days`.
// selection.legacy: delivery chosen by an older client that cannot send a location (skips that check).
// Returns { lines } or { error: '<code>' }. Nothing is selected by default.
export function selectOptions(listingOptions, selections, { days, disabledCodes = [] } = {}) {
  const available = Array.isArray(listingOptions) ? listingOptions : [];
  const requested = Array.isArray(selections) ? selections : [];
  const numberOfDays = Number(days);
  if (!Number.isInteger(numberOfDays) || numberOfDays < 1) return { error: 'invalidDays' };

  const seen = new Set();
  const lines = [];
  for (const selection of requested) {
    const option = available.find((item) => Number(item.id) === Number(selection && selection.id));
    if (!option) return { error: 'unknownOption' };
    if (seen.has(option.id)) return { error: 'duplicateOption' };
    seen.add(option.id);
    if (!isOptionBookable(option, disabledCodes)) return { error: 'optionUnavailable' };
    if (!BILLING_MODES.includes(option.billingMode)) return { error: 'invalidBillingMode' };

    let userInput = null;
    if (option.code === 'delivery' && !selection.legacy) {
      userInput = typeof selection.userInput === 'string' ? selection.userInput.trim() : '';
      if (userInput.length < OPTION_LIMITS.deliveryLocationMin) return { error: 'deliveryLocationRequired' };
      if (userInput.length > OPTION_LIMITS.deliveryLocationMax) return { error: 'deliveryLocationTooLong' };
    }

    const quantity = option.billingMode === 'per_day' ? numberOfDays : 1;
    const unitPrice = Number(option.price);
    lines.push({
      listingOptionId: option.id,
      code: option.code,
      isFixed: Boolean(option.isFixed),
      label: option.label,
      labelKey: option.isFixed ? option.code : null,
      description: option.description || null,
      billingMode: option.billingMode,
      unitPrice,
      quantity,
      amount: round2(unitPrice * quantity),
      userInput: userInput || null,
    });
  }
  return { lines };
}

// Computes the booking amounts in the listing currency.
// fees: { guest: { type, value, fixedAmount }, host: { type, value, fixedAmount } }
export function computeBooking({
  priceForDays = 0,
  discount = 0,
  promoDiscount = 0,
  promoDeductionType = null,
  options = [],
  fees = {},
  securityDeposit = 0,
}) {
  const optionLines = options.map((option) => ({ ...option, amount: round2(option.amount) }));
  const optionsTotal = round2(optionLines.reduce((sum, option) => sum + option.amount, 0));
  const rentalAfterDiscounts = Number(priceForDays) - Number(discount) - Number(promoDiscount);
  const feeBase = rentalAfterDiscounts + optionsTotal;

  const guest = fees.guest || {};
  const host = fees.host || {};
  const guestServiceFee = guest.type === 'percentage'
    ? feeBase * (Number(guest.value) / 100)
    : Number(guest.fixedAmount || 0);
  const hostFeeBase = promoDeductionType === 'admin' ? feeBase + Number(promoDiscount) : feeBase;
  const hostServiceFee = host.type === 'percentage'
    ? hostFeeBase * (Number(host.value) / 100)
    : Number(host.fixedAmount || 0);

  const deposit = Number(securityDeposit) || 0;
  return {
    options: optionLines,
    optionsTotal,
    rentalAfterDiscounts,
    feeBase,
    guestServiceFee,
    hostServiceFee,
    total: feeBase,
    totalPaid: feeBase + guestServiceFee + deposit,
    hostPayout: feeBase - hostServiceFee + (promoDeductionType === 'admin' ? Number(promoDiscount) : 0),
  };
}

// ---------------------------------------------------------------------------
// Option configuration rules (owner side). Same file in GOZY-API and GOZY-WEB.
// ---------------------------------------------------------------------------

export const FIXED_OPTIONS = {
  delivery: { slot: 'delivery', code: 'delivery', billingMode: 'flat', icon: 'truck', sortOrder: 0 },
  chauffeur: { slot: 'chauffeur', code: 'chauffeur', billingMode: 'per_day', icon: 'driver', sortOrder: 1 },
};

// Platform settings managed by the admin: how many custom options an owner may add (default 3, the client rule)
// and which fixed services are switched on. Custom slots are named custom_1, custom_2, ... (no fixed list).
export const SETTINGS_LIMITS = { maxCustomOptionsCeiling: 100 };
export const DEFAULT_OPTION_SETTINGS = { maxCustomOptions: 3, fixedEnabled: { delivery: true, chauffeur: true } };
export const CUSTOM_SLOT_PATTERN = /^custom_[1-9][0-9]{0,2}$/;
export const customSlotNumber = (slot) => Number(String(slot).slice('custom_'.length));

const toBool = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  return !(value === false || value === 0 || ['false', '0', 'no', 'off'].includes(String(value).toLowerCase()));
};

// raw: { maxCustomOptions, deliveryEnabled, chauffeurEnabled } as stored (strings, numbers, booleans or missing).
export function normalizeOptionSettings(raw) {
  const source = raw || {};
  const parsed = Number(source.maxCustomOptions);
  const maxCustomOptions = source.maxCustomOptions === null || source.maxCustomOptions === undefined || source.maxCustomOptions === '' || !Number.isFinite(parsed)
    ? DEFAULT_OPTION_SETTINGS.maxCustomOptions
    : Math.min(SETTINGS_LIMITS.maxCustomOptionsCeiling, Math.max(0, Math.floor(parsed)));
  return {
    maxCustomOptions,
    fixedEnabled: {
      delivery: toBool(source.deliveryEnabled, true),
      chauffeur: toBool(source.chauffeurEnabled, true),
    },
  };
}

export const disabledFixedCodes = (settings) => Object.keys(FIXED_OPTIONS)
  .filter((code) => !((settings || DEFAULT_OPTION_SETTINGS).fixedEnabled || {})[code]);

const RESERVED_LABELS = [
  'livraison', 'livraison du vehicule', 'delivery', 'vehicle delivery',
  'chauffeur', 'location avec chauffeur', 'driver', 'with driver',
];

export const normalizeLabel = (value) => String(value || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

const cleanText = (value) => String(value == null ? '' : value).replace(/<[^>]*>/g, '').replace(/[<>]/g, '').replace(/\s+/g, ' ').trim();

export function firstFreeCustomSlot(rows) {
  const used = new Set((rows || []).map((row) => row.slot));
  for (let number = 1; number <= SETTINGS_LIMITS.maxCustomOptionsCeiling; number += 1) {
    if (!used.has(`custom_${number}`)) return `custom_${number}`;
  }
  return null;
}

// Owner view: the fixed options switched on by the admin always listed (inactive virtual rows when absent),
// then the custom ones (an owner above a lowered limit keeps the options already created).
export function buildOwnerOptionList(rows, settings = DEFAULT_OPTION_SETTINGS) {
  const list = Array.isArray(rows) ? rows : [];
  const disabled = disabledFixedCodes(settings);
  const fixed = Object.values(FIXED_OPTIONS).filter((definition) => !disabled.includes(definition.code)).map((definition) => {
    const row = list.find((item) => item.slot === definition.slot);
    return row || {
      id: null, slot: definition.slot, code: definition.code, isFixed: true, label: null, description: null,
      price: 0, billingMode: definition.billingMode, isActive: false, adminDisabled: false,
      icon: definition.icon, sortOrder: definition.sortOrder,
    };
  });
  const custom = list.filter((item) => !item.isFixed)
    .sort((a, b) => customSlotNumber(a.slot) - customSlotNumber(b.slot) || String(a.slot).localeCompare(String(b.slot)));
  return [...fixed, ...custom];
}

// Validates and normalizes an owner write. `rows` = current options of the listing.
// Returns { value } (fields to persist, slot resolved) or { error: '<code>' }.
export function validateOptionInput(input, rows, settings = DEFAULT_OPTION_SETTINGS) {
  const existing = Array.isArray(rows) ? rows : [];
  const wanted = input || {};
  const limit = normalizeOptionSettings({
    maxCustomOptions: settings.maxCustomOptions,
    deliveryEnabled: settings.fixedEnabled && settings.fixedEnabled.delivery,
    chauffeurEnabled: settings.fixedEnabled && settings.fixedEnabled.chauffeur,
  });
  const customCount = existing.filter((row) => !row.isFixed).length;
  if (!wanted.slot && customCount >= limit.maxCustomOptions) return { error: 'maxOptionsReached' };
  const slot = wanted.slot || firstFreeCustomSlot(existing);
  const fixed = FIXED_OPTIONS[slot];
  if (!slot || (!fixed && !CUSTOM_SLOT_PATTERN.test(slot))) {
    return { error: wanted.slot ? 'invalidOptionSlot' : 'maxOptionsReached' };
  }
  if (fixed && !limit.fixedEnabled[fixed.code]) return { error: 'optionDisabledByAdmin' };

  const current = existing.find((row) => row.slot === slot);
  const price = Number(wanted.price);
  if (!Number.isFinite(price) || price < 0 || price > OPTION_LIMITS.maxPrice) return { error: 'invalidOptionPrice' };
  const isActive = Boolean(wanted.isActive);
  if (isActive && price <= 0) return { error: 'invalidOptionPrice' };

  if (fixed) {
    return {
      value: {
        slot, code: fixed.code, isFixed: true, label: null, description: null,
        price: Math.round(price * 100) / 100, billingMode: fixed.billingMode, isActive,
        icon: fixed.icon, sortOrder: fixed.sortOrder,
      },
    };
  }

  const label = cleanText(wanted.label);
  if (label.length < OPTION_LIMITS.labelMin || label.length > OPTION_LIMITS.labelMax) return { error: 'invalidOptionLabel' };
  const normalized = normalizeLabel(label);
  if (RESERVED_LABELS.includes(normalized)) return { error: 'reservedOptionLabel' };
  if (existing.some((row) => row.slot !== slot && normalizeLabel(row.label) === normalized)) return { error: 'optionLabelTaken' };
  const description = cleanText(wanted.description);
  if (description.length > OPTION_LIMITS.descriptionMax) return { error: 'invalidOptionDescription' };
  if (!BILLING_MODES.includes(wanted.billingMode)) return { error: 'invalidBillingMode' };
  if (!current && customCount >= limit.maxCustomOptions) return { error: 'maxOptionsReached' };

  return {
    value: {
      slot, code: 'custom', isFixed: false, label, description: description || null,
      price: Math.round(price * 100) / 100, billingMode: wanted.billingMode, isActive,
      icon: cleanText(wanted.icon).slice(0, 24) || null,
      sortOrder: 10 + customSlotNumber(slot),
    },
  };
}
